import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'react-bootstrap'

import { GET, POST, DELETE, httpOk } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

// Свои активные сеансы — как «Активные сеансы» в Telegram: это устройство, остальные,
// завершить один или все сразу. Сервер отдаёт только входы с собственным токеном —
// касса и входы до 18.10.2026 на общем токене сюда не попадают: завершить их нельзя.

// Иконка и цвет кружка по приложению — как платформы в списке Telegram
const APP_STYLES = {
	web: { icon: 'uil-desktop', color: '#3b82f6' },
	owner: { icon: 'uil-mobile-android', color: '#22c55e' },
	delivery: { icon: 'uil-truck', color: '#f59e0b' },
	cashier: { icon: 'uil-store', color: '#a855f7' },
	terminal: { icon: 'uil-tablet', color: '#14b8a6' },
}

// Названия платформ не переводятся — как в Telegram
const PLATFORM_NAMES = {
	android: 'Android',
	ios: 'iPhone',
}

const INTL_LOCALES = {
	ru: 'ru',
	uz_latn: 'uz',
	uz_cyrl: 'uz-Cyrl',
	tr: 'tr',
}

// Последняя активность как в Telegram: сегодня — время, за неделю — день недели («вс»),
// в этом году — «11 сент.», раньше — полная дата
function formatLastSeen(value, language) {
	if (!value) return null
	const date = new Date(String(value).replace(' ', 'T'))
	if (isNaN(date.getTime())) return null

	const locale = INTL_LOCALES[language] || 'ru'
	const now = new Date()
	const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
	const daysAgo = Math.floor((startOfToday - new Date(date.getFullYear(), date.getMonth(), date.getDate())) / 86400000)

	if (daysAgo <= 0) return formatDate(date, 'HH:mm')
	if (daysAgo < 7) return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date)
	if (date.getFullYear() === now.getFullYear()) {
		return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(date)
	}
	return formatDate(date, 'dd.MM.yyyy')
}

const sectionTitleStyle = {
	color: 'var(--dark-primary, #3b82f6)',
	fontSize: '14px',
	fontWeight: 600,
	padding: '14px 20px 6px',
}

const sectionGapStyle = {
	background: 'var(--dark-muted-bg, #f1f3f6)',
	color: 'var(--dark-text-muted, #8791a1)',
	fontSize: '12px',
	padding: '10px 20px',
}

function DevicesModal({ show, setShow }) {
	const { t, i18n } = useTranslation()

	const [sessions, setSessions] = useState([])
	const [loaded, setLoaded] = useState(false)
	const [confirmOthers, setConfirmOthers] = useState(false)

	async function getSessions() {
		const response = await GET('/sessions/my', {}, { loader: false })
		if (httpOk(response)) setSessions(response.data || [])
		setLoaded(true)
	}

	async function revoke(item) {
		const response = await DELETE(`/sessions/my/${item.id}`, {}, { loader: true })
		if (httpOk(response)) getSessions()
	}

	async function revokeOthers() {
		const response = await POST('/sessions/my/terminate-others', {}, { loader: true })
		setConfirmOthers(false)
		if (httpOk(response)) getSessions()
	}

	function appName(item) {
		const key = 'audit_source_' + (item.app_code || 'web')
		return t(key) !== key ? t(key) : (item.app_code || 'web')
	}

	function renderSession(item, isLast) {
		const style = APP_STYLES[item.app_code] || APP_STYLES.web
		// Заголовок — браузер или модель телефона. Приложения до 19.10.2026 модель не слали —
		// тогда хотя бы платформа, а без неё само приложение (и второй строкой его не повторяем)
		const deviceName = item.device_model || PLATFORM_NAMES[item.platform] || null
		const title = deviceName || appName(item)
		const subtitle = [
			deviceName ? appName(item) + (item.app_version ? ' ' + item.app_version : '') : null,
			item.pos_name,
		].filter(Boolean).join(' · ')
		// Как в Telegram: «Ташкент, Узбекистан • вс». Город не определился — тогда IP;
		// сам IP всегда в подсказке
		const meta = [
			item.location || item.ip,
			item.is_current ? t('devices_online') : formatLastSeen(item.last_seen_at, i18n.language),
		].filter(Boolean).join(' • ')

		return (
			<div key={item.id} className="d-flex align-items-center"
				style={{ gap: '14px', padding: '10px 20px' }}>
				<div className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
					style={{ width: '42px', height: '42px', fontSize: '21px', background: style.color, color: '#fff' }}>
					<i className={style.icon}></i>
				</div>
				<div className="flex-grow-1"
					style={{
						minWidth: 0,
						paddingBottom: isLast ? 0 : '10px',
						marginBottom: isLast ? 0 : '-10px',
						borderBottom: isLast ? 'none' : '1px solid var(--dark-border, #edf0f4)',
					}}>
					<div className="text-truncate" style={{ fontWeight: 600 }}>{title}</div>
					{subtitle && <div className="text-truncate">{subtitle}</div>}
					<div className="text-truncate" title={item.ip || undefined}
						style={{ fontSize: '12px', color: 'var(--dark-text-muted, #8791a1)' }}>{meta}</div>
				</div>
				{!item.is_current && item.can_revoke &&
					<button type="button" className="btn btn-sm btn-light rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center"
						style={{ width: '32px', height: '32px', padding: 0 }}
						title={t('devices_terminate')}
						onClick={() => revoke(item)}>
						<i className="uil-times" style={{ fontSize: '18px' }}></i>
					</button>
				}
			</div>
		)
	}

	useEffect(() => {
		if (show) {
			setLoaded(false)
			setConfirmOthers(false)
			getSessions()
		}
	}, [show]) // eslint-disable-line react-hooks/exhaustive-deps

	const current = sessions.filter(item => item.is_current)
	const others = sessions.filter(item => !item.is_current && item.can_revoke)

	return (
		<Modal show={show} onHide={() => setShow(false)} scrollable centered>
			<Modal.Header closeButton>
				<Modal.Title>{t('devices_title')}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ height: '500px', padding: 0 }}>
				{current.length > 0 &&
					<>
						<div style={sectionTitleStyle}>{t('devices_this_device')}</div>
						{current.map((item, index) => renderSession(item, index === current.length - 1))}
					</>
				}

				{others.length > 0 &&
					<div style={{ padding: '6px 20px 12px' }}>
						{/* Подтверждение — в той же строке, маленькими кнопками справа, а не двумя
						    кнопками на всю ширину: высота блока не прыгает */}
						{confirmOthers
							? <div className="d-flex align-items-center text-danger" style={{ gap: '14px' }}>
								<span className="d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '42px', fontSize: '22px' }}>
									<i className="uil-signout"></i>
								</span>
								<span className="flex-grow-1">{t('devices_terminate_others')}?</span>
								<button type="button" className="btn btn-sm btn-light flex-shrink-0" onClick={() => setConfirmOthers(false)}>
									{t('cancel')}
								</button>
								<button type="button" className="btn btn-sm btn-danger flex-shrink-0" onClick={revokeOthers}>
									{t('audit_session_terminate')}
								</button>
							</div>
							: <button type="button" className="btn btn-link text-danger p-0 d-flex align-items-center text-decoration-none"
								style={{ gap: '14px' }}
								onClick={() => setConfirmOthers(true)}>
								<span className="d-flex align-items-center justify-content-center" style={{ width: '42px', fontSize: '22px' }}>
									<i className="uil-signout"></i>
								</span>
								{t('devices_terminate_others')}
							</button>
						}
					</div>
				}

				{(current.length > 0 || others.length > 0) &&
					<div style={sectionGapStyle}>{t('devices_terminate_others_hint')}</div>
				}

				{others.length > 0 &&
					<>
						<div style={sectionTitleStyle}>{t('devices_active_sessions')}</div>
						{others.map((item, index) => renderSession(item, index === others.length - 1))}
					</>
				}

				{loaded && sessions.length === 0 &&
					<div className="text-center mt-5" style={{ color: 'var(--dark-text-muted, #8791a1)' }}>{t('devices_empty')}</div>
				}

				{/* Лицензия DB-IP Lite (CC BY 4.0) требует указать источник местоположения */}
				{sessions.some(item => item.location) &&
					<div style={{ ...sectionGapStyle, marginTop: '10px' }}>
						{t('devices_geo_source')} <a href="https://db-ip.com" target="_blank" rel="noopener noreferrer">DB-IP</a>
					</div>
				}
			</Modal.Body>
		</Modal>
	)
}

export default DevicesModal
